import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { type Control, type FieldValues, type Path, type PathValue, type UseFormReturn, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { api, type AddressSuggestion, type BuyerAddress, type BuyerAddressInput, type CitySuggestion } from "@/api";
import { Button } from "@osmix/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@osmix/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { dialCode, formatDisplay, formatNational } from "@osmix/lib/phone";
export type AddressCoreValues = {
  country: string;
  city: string;
  street: string;
  postalCode: string;
  comment: string;
};
export type BuyerAddressFormValues = AddressCoreValues & {
  label: string;
  recipient: string;
  phone: string;
  isDefault: boolean;
};
export type BuyerAddressContactDefaults = {
  recipient?: string;
  phone?: string;
};
export const addressLine = (address: Pick<BuyerAddress, "city" | "street" | "postalCode">): string => [address.city, address.street, address.postalCode].filter(Boolean).join(", ");
export const formatAddressPhone = (phone: string): string => {
  const trimmed = phone.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return formatDisplay(trimmed);
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return trimmed;
  return `${dialCode("RU")} ${formatNational(digits, "RU")}`;
};
export const buyerAddressFormDefaults = (address?: BuyerAddress | null, contact?: BuyerAddressContactDefaults): BuyerAddressFormValues => ({
  label: address?.label ?? "",
  recipient: address?.recipient || contact?.recipient || "",
  phone: address?.phone || contact?.phone || "",
  country: address?.country ?? "Россия",
  city: address?.city ?? "",
  street: address?.street ?? "",
  postalCode: address?.postalCode ?? "",
  comment: address?.comment ?? "",
  isDefault: address?.isDefault ?? false
});
export const buyerAddressInputFromValues = (values: BuyerAddressFormValues): BuyerAddressInput => ({
  label: values.label || values.street,
  recipient: values.recipient,
  phone: values.phone,
  country: values.country,
  city: values.city,
  street: values.street,
  postalCode: values.postalCode,
  comment: values.comment ?? "",
  isDefault: values.isDefault
});
export const buyerAddressSchema = (t: (key: string) => string) => z.object({
  label: z.string(),
  recipient: z.string().min(1, t("checkout.required")),
  phone: z.string().min(1, t("checkout.required")),
  country: z.string().min(1, t("checkout.required")),
  city: z.string().min(1, t("checkout.required")),
  street: z.string().min(1, t("checkout.required")),
  postalCode: z.string().min(1, t("checkout.required")),
  comment: z.string(),
  isDefault: z.boolean()
});
type AddressFieldNames<T extends FieldValues> = {
  country: Path<T>;
  city: Path<T>;
  street: Path<T>;
  postalCode: Path<T>;
  comment: Path<T>;
};
const defaultNames = {
  country: "country",
  city: "city",
  street: "street",
  postalCode: "postalCode",
  comment: "comment"
};
const fieldValue = <T extends FieldValues,>(value: string) => value as PathValue<T, Path<T>>;
export function AddressAutocompleteFields(t0) {
  const $ = _c(62);
  const {
    form,
    names: t1,
    className
  } = t0;
  const names = t1 === undefined ? defaultNames as AddressFieldNames<T> : t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[0] !== form || $[1] !== names.city) {
    t2 = form.watch(names.city) ?? "";
    $[0] = form;
    $[1] = names.city;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const watchCity = String(t2);
  let t3;
  if ($[3] !== form || $[4] !== names.city || $[5] !== names.country || $[6] !== names.postalCode || $[7] !== names.street) {
    t3 = a => {
      if (a.street) {
        form.setValue(names.street, fieldValue(a.street), {
          shouldValidate: true
        });
      }
      if (a.city) {
        form.setValue(names.city, fieldValue(a.city), {
          shouldValidate: true
        });
      }
      if (a.postalCode) {
        form.setValue(names.postalCode, fieldValue(a.postalCode), {
          shouldValidate: true
        });
      }
      if (a.country) {
        form.setValue(names.country, fieldValue(a.country), {
          shouldValidate: true
        });
      }
    };
    $[3] = form;
    $[4] = names.city;
    $[5] = names.country;
    $[6] = names.postalCode;
    $[7] = names.street;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const applyAddress = t3;
  let t4;
  if ($[9] !== form || $[10] !== names.city || $[11] !== names.country || $[12] !== names.postalCode) {
    t4 = c => {
      if (c.city) {
        form.setValue(names.city, fieldValue(c.city), {
          shouldValidate: true
        });
      }
      if (c.country) {
        form.setValue(names.country, fieldValue(c.country), {
          shouldValidate: true
        });
      }
      if (c.postalCode) {
        form.setValue(names.postalCode, fieldValue(c.postalCode), {
          shouldValidate: true
        });
      }
    };
    $[9] = form;
    $[10] = names.city;
    $[11] = names.country;
    $[12] = names.postalCode;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const applyCity = t4;
  const t5 = className ?? "grid gap-4 sm:grid-cols-2";
  const t6 = form.control;
  const t7 = names.country;
  let t8;
  if ($[14] !== t) {
    t8 = t("checkout.country");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== form.control || $[17] !== names.country || $[18] !== t8) {
    t9 = <Field control={t6} name={t7} label={t8} />;
    $[16] = form.control;
    $[17] = names.country;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = form.control;
  const t11 = names.city;
  let t12;
  if ($[20] !== t) {
    t12 = t("checkout.city");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t) {
    t13 = t("checkout.searchCity");
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== applyCity || $[25] !== form.control || $[26] !== names.city || $[27] !== t12 || $[28] !== t13) {
    t14 = <SuggestField control={t10} name={t11} label={t12} placeholder={t13} suggest={_temp} onPick={applyCity} />;
    $[24] = applyCity;
    $[25] = form.control;
    $[26] = names.city;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = form.control;
  const t16 = names.street;
  let t17;
  if ($[30] !== t) {
    t17 = t("checkout.street");
    $[30] = t;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t) {
    t18 = t("checkout.searchAddress");
    $[32] = t;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== watchCity) {
    t19 = q_0 => api.geo.addressSuggest(q_0, watchCity.trim() || undefined);
    $[34] = watchCity;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== applyAddress || $[37] !== form.control || $[38] !== names.street || $[39] !== t17 || $[40] !== t18 || $[41] !== t19) {
    t20 = <SuggestField control={t15} name={t16} label={t17} className="sm:col-span-2" placeholder={t18} suggest={t19} onPick={applyAddress} />;
    $[36] = applyAddress;
    $[37] = form.control;
    $[38] = names.street;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const t21 = form.control;
  const t22 = names.postalCode;
  let t23;
  if ($[43] !== t) {
    t23 = t("checkout.postalCode");
    $[43] = t;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== form.control || $[46] !== names.postalCode || $[47] !== t23) {
    t24 = <Field control={t21} name={t22} label={t23} />;
    $[45] = form.control;
    $[46] = names.postalCode;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t) {
    t25 = t26 => {
      const {
        field
      } = t26;
      return <FormItem className="sm:col-span-2"><FormLabel>{t("checkout.commentOptional")}</FormLabel><FormControl><Textarea rows={2} {...field} value={String(field.value ?? "")} /></FormControl><FormMessage /></FormItem>;
    };
    $[49] = t;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== form.control || $[52] !== names.comment || $[53] !== t25) {
    t26 = <FormField control={form.control} name={names.comment} render={t25} />;
    $[51] = form.control;
    $[52] = names.comment;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t14 || $[56] !== t20 || $[57] !== t24 || $[58] !== t26 || $[59] !== t5 || $[60] !== t9) {
    t27 = <div className={t5}>{t9}{t14}{t20}{t24}{t26}</div>;
    $[55] = t14;
    $[56] = t20;
    $[57] = t24;
    $[58] = t26;
    $[59] = t5;
    $[60] = t9;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
}
function _temp(q) {
  return api.geo.citySuggest(q);
}
export function BuyerAddressFormFields(t0) {
  const $ = _c(16);
  const {
    form
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = form.control;
  let t2;
  if ($[0] !== t) {
    t2 = t("profile.address.label");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== form.control || $[3] !== t2) {
    t3 = <Field control={t1} name="label" label={t2} className="sm:col-span-2" />;
    $[2] = form.control;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== form) {
    t4 = <div className="sm:col-span-2"><AddressAutocompleteFields form={form} /></div>;
    $[5] = form;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t6 => {
      const {
        field
      } = t6;
      return <FormItem className="sm:col-span-2 flex items-center gap-2 space-y-0"><FormControl><Checkbox checked={field.value} onCheckedChange={checked => field.onChange(Boolean(checked))} /></FormControl><FormLabel className="type-body2 text-osmix-text-1">{t("profile.address.isDefault")}</FormLabel></FormItem>;
    };
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== form.control || $[10] !== t5) {
    t6 = <FormField control={form.control} name="isDefault" render={t5} />;
    $[9] = form.control;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t6) {
    t7 = <div className="grid gap-4 sm:grid-cols-2">{t3}{t4}{t6}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
export function AddressFormDialog(t0) {
  const $ = _c(61);
  const {
    open,
    onOpenChange,
    address,
    contact,
    onSubmit,
    submitting: t1
  } = t0;
  const submitting = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[0] !== t) {
    t2 = zodResolver(buyerAddressSchema(t));
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== address || $[3] !== contact) {
    t3 = buyerAddressFormDefaults(address, contact);
    $[2] = address;
    $[3] = contact;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      resolver: t2,
      defaultValues: t3
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const form = useForm(t4);
  let t5;
  let t6;
  if ($[8] !== address || $[9] !== contact || $[10] !== form || $[11] !== open) {
    t5 = () => {
      if (open) {
        form.reset(buyerAddressFormDefaults(address, contact));
      }
    };
    t6 = [address, contact, form, open];
    $[8] = address;
    $[9] = contact;
    $[10] = form;
    $[11] = open;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== address || $[15] !== t) {
    t7 = address ? t("profile.editAddress") : t("profile.addAddress");
    $[14] = address;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <DialogTitle>{t7}</DialogTitle>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t) {
    t9 = t("profile.addressFormHint");
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <DialogDescription>{t9}</DialogDescription>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <DialogHeader>{t8}{t10}</DialogHeader>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== form || $[27] !== onSubmit) {
    t12 = form.handleSubmit(onSubmit);
    $[26] = form;
    $[27] = onSubmit;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== form) {
    t13 = <BuyerAddressFormFields form={form} />;
    $[29] = form;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== onOpenChange) {
    t14 = () => onOpenChange(false);
    $[31] = onOpenChange;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t) {
    t15 = t("common.cancel");
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t14 || $[36] !== t15) {
    t16 = <Button type="button" variant="osmix-outline" size="pill" onClick={t14}>{t15}</Button>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== submitting || $[39] !== t) {
    t17 = submitting ? t("checkout.processing") : t("profile.save");
    $[38] = submitting;
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== submitting || $[42] !== t17) {
    t18 = <Button type="submit" variant="osmix-filled" size="pill" disabled={submitting}>{t17}</Button>;
    $[41] = submitting;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t16 || $[45] !== t18) {
    t19 = <DialogFooter>{t16}{t18}</DialogFooter>;
    $[44] = t16;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t12 || $[48] !== t13 || $[49] !== t19) {
    t20 = <form onSubmit={t12} className="flex flex-col gap-5">{t13}{t19}</form>;
    $[47] = t12;
    $[48] = t13;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== form || $[52] !== t20) {
    t21 = <Form {...form}>{t20}</Form>;
    $[51] = form;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t11 || $[55] !== t21) {
    t22 = <DialogContent className="max-h-[90vh] overflow-auto rounded-2xl sm:max-w-2xl">{t11}{t21}</DialogContent>;
    $[54] = t11;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== onOpenChange || $[58] !== open || $[59] !== t22) {
    t23 = <Dialog open={open} onOpenChange={onOpenChange}>{t22}</Dialog>;
    $[57] = onOpenChange;
    $[58] = open;
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  return t23;
}
function Field(t0) {
  const $ = _c(9);
  const {
    control,
    name,
    label,
    type,
    placeholder,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== label || $[2] !== placeholder || $[3] !== type) {
    t1 = t2 => {
      const {
        field
      } = t2;
      return <FormItem className={className}><FormLabel>{label}</FormLabel><FormControl><Input type={type} placeholder={placeholder} {...field} value={String(field.value ?? "")} /></FormControl><FormMessage /></FormItem>;
    };
    $[0] = className;
    $[1] = label;
    $[2] = placeholder;
    $[3] = type;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== control || $[6] !== name || $[7] !== t1) {
    t2 = <FormField control={control} name={name} render={t1} />;
    $[5] = control;
    $[6] = name;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function SuggestField(t0) {
  const $ = _c(10);
  const {
    control,
    name,
    label,
    className,
    placeholder,
    suggest,
    onPick
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== label || $[2] !== onPick || $[3] !== placeholder || $[4] !== suggest) {
    t1 = t2 => {
      const {
        field
      } = t2;
      return <FormItem className={className}><FormLabel>{label}</FormLabel><SuggestControl name={field.name} value={String(field.value ?? "")} onChange={field.onChange} placeholder={placeholder} suggest={suggest} onPick={onPick} /><FormMessage /></FormItem>;
    };
    $[0] = className;
    $[1] = label;
    $[2] = onPick;
    $[3] = placeholder;
    $[4] = suggest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== control || $[7] !== name || $[8] !== t1) {
    t2 = <FormField control={control} name={name} render={t1} />;
    $[6] = control;
    $[7] = name;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function SuggestControl(t0) {
  const $ = _c(38);
  const {
    name,
    value,
    onChange,
    placeholder,
    suggest,
    onPick
  } = t0;
  const {
    t
  } = useTranslation();
  const boxRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  let t1;
  let t2;
  if ($[0] !== value) {
    t1 = () => {
      const id = setTimeout(() => setQuery(value.trim()), 300);
      return () => clearTimeout(id);
    };
    t2 = [value];
    $[0] = value;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== name || $[4] !== open || $[5] !== query) {
    t3 = ["geo", "suggest", name, open, query];
    $[3] = name;
    $[4] = open;
    $[5] = query;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== open || $[8] !== query || $[9] !== suggest) {
    t4 = () => open && query.length >= 3 ? suggest(query) : Promise.resolve([] as S[]);
    $[7] = open;
    $[8] = query;
    $[9] = suggest;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4
    };
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const suggestions = useQuery(t5);
  let t6;
  if ($[14] !== suggestions.data) {
    t6 = suggestions.data ?? [];
    $[14] = suggestions.data;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const list = t6;
  let t7;
  let t8;
  if ($[16] !== open) {
    t7 = () => {
      if (!open) {
        return;
      }
      const onDown = e => {
        if (!boxRef.current?.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    };
    t8 = [open];
    $[16] = open;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  let t9;
  if ($[19] !== onChange) {
    t9 = e_0 => {
      onChange(e_0.target.value);
      setOpen(true);
    };
    $[19] = onChange;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setOpen(true);
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== name || $[23] !== placeholder || $[24] !== t9 || $[25] !== value) {
    t11 = <Input name={name} value={value} onChange={t9} onFocus={t10} placeholder={placeholder} autoComplete="off" />;
    $[22] = name;
    $[23] = placeholder;
    $[24] = t9;
    $[25] = value;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== list || $[28] !== onChange || $[29] !== onPick || $[30] !== open || $[31] !== query || $[32] !== suggestions.isPending || $[33] !== t) {
    t12 = open && query.length >= 3 && <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-osmix-gray-300 bg-card py-1 shadow-block">{suggestions.isPending ? <p className="px-3 py-2 type-small text-osmix-text-2">{t("checkout.searchLoading")}</p> : list.length === 0 ? <p className="px-3 py-2 type-small text-osmix-text-2">{t("checkout.searchEmpty")}</p> : list.map(a => <button key={a.value} type="button" onClick={() => {
        onPick(a);
        onChange(a.value);
        setOpen(false);
      }} className="block w-full px-3 py-2 text-left type-body2 text-osmix-text-1 hover:bg-osmix-gray-100">{a.value}</button>)}</div>;
    $[27] = list;
    $[28] = onChange;
    $[29] = onPick;
    $[30] = open;
    $[31] = query;
    $[32] = suggestions.isPending;
    $[33] = t;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t11 || $[36] !== t12) {
    t13 = <div ref={boxRef} className="relative">{t11}{t12}</div>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
}